import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  FiFileText, FiAlertTriangle, FiDownload, FiList, FiSearch, FiX,
  FiUser, FiRefreshCw, FiPackage, FiSlash, FiEye,
} from 'react-icons/fi';
import {
  baixarDanfse, baixarXmlNota, listarNotasPeriodo, baixarNotasEmLote, cancelarNotaFiscal,
} from '../../services/nf';
import { listarClientes } from '../../services/clientes';
import AssistenteEmissao from './emitir';
import { formatarDocumento } from '../../utils/documento';
import styles from './Nf.module.css';
import useEsc from '../../hooks/useEsc';
import { ehPortal } from '../../utils/portal';

const ABAS = [
  { id: 'emitir', label: 'NF', icon: FiFileText },
  { id: 'notas',  label: 'Emitidas', icon: FiList },
];

function formatarDataHora(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR');
}

function formatarCompetencia(data) {
  if (!data) return '—';
  const [ano, mes] = String(data).slice(0, 10).split('-');
  return mes ? `${mes}/${ano}` : data;
}

function formatarMoeda(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return v || '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function mesAtual() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/* o filtro da tela é por MÊS, mas a competência é uma DATA e nem sempre
   cai no dia 1 (as notas reais vieram com 02/10 e 13/07). Por isso o fim
   do intervalo é o último dia do mês escolhido -- fechar no dia 1
   esconderia justamente as notas do mês que a pessoa pediu. */
const primeiroDia = (mes) => (mes ? `${mes}-01` : null);

function ultimoDia(mes) {
  if (!mes) return null;
  const [ano, m] = mes.split('-').map(Number);
  return `${mes}-${String(new Date(ano, m, 0).getDate()).padStart(2, '0')}`;
}

function baixarBlob(blob, nome) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ── escolha do cliente que vai EMITIR a nota ──
   Lista só quem está marcado como NF-Essensials no cadastro: é pra isso
   que a marcação existe, e é ela que separa "cliente do escritório" de
   "cliente que emite nota por aqui". */
/* Na própria tela inicial do emissor (era um modal atrás de um botão --
   um clique a mais sem motivo). */
function ListaClientesEmissores({ clientes, onSelecionar }) {
  const [busca, setBusca] = useState('');

  const emissores = clientes.filter(c => c.nfEssensials);
  const termo = busca.trim().toLowerCase();
  const digitos = busca.replace(/\D/g, '');
  const visiveis = termo
    ? emissores.filter(c =>
        c.nome?.toLowerCase().includes(termo) ||
        (c.codigo || '').toLowerCase().includes(termo) ||
        (digitos && (c.cnpj || c.cpf || '').includes(digitos)))
    : emissores;

  return (
    <div style={{ width: '100%', maxWidth: 560, margin: '18px auto 0', textAlign: 'left' }}>
      <div>

        {/* busca só quando a lista é grande o bastante pra justificar */}
        {emissores.length > 8 && (
          <div style={{ padding: '0 0 10px' }}>
            <div style={{ position: 'relative' }}>
              <FiSearch size={13} style={{ position: 'absolute', left: 10, top: 11, color: '#9ab0c4' }} />
              <input className={styles.campoInput} style={{ paddingLeft: 30 }} autoFocus
                placeholder="Código, nome ou CNPJ..."
                value={busca} onChange={e => setBusca(e.target.value)} />
            </div>
          </div>
        )}

        <div>
          {visiveis.length === 0 ? (
            <p className={styles.vazio} style={{ padding: '20px 0' }}>
              {emissores.length === 0
                ? 'Nenhum cliente está marcado como NF-Essensials no cadastro. Marque no cadastro do cliente para ele aparecer aqui.'
                : 'Nenhum cliente encontrado com esse termo.'}
            </p>
          ) : (
            <div className={styles.listaClientes}>
              {visiveis.map(c => (
                <button key={c.id} type="button" className={styles.itemCliente}
                  onClick={() => onSelecionar(c)}>
                  <span className={styles.itemClienteIcone}><FiUser size={14} /></span>
                  <span style={{ minWidth: 0 }}>
                    <span className={styles.itemClienteNome}>
                      <strong style={{ color: '#6B8CAE', marginRight: 6 }}>{c.codigo}</strong>
                      {c.nome}
                    </span>
                    <span className={styles.itemClienteDoc}>
                      {formatarDocumento(c.cnpj || c.cpf) || 'sem documento'}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Cancelamento ──
   NFS-e autorizada não se apaga: registra-se um evento de cancelamento na
   Receita, e a nota passa a constar como cancelada. Como é irreversível e
   tem efeito fiscal, o modal mostra a nota inteira (número, tomador,
   valor) antes de qualquer botão -- cancelar a nota errada é o risco real
   aqui, não errar o motivo. */
const MOTIVOS_CANCELAMENTO = [
  { codigo: '1', nome: 'Erro na emissão' },
  { codigo: '2', nome: 'Serviço não prestado' },
  { codigo: '9', nome: 'Outros' },
];

function ModalCancelar({ nota, onConfirmado, onFechar }) {
  useEsc(onFechar);
  const [cMotivo, setCMotivo] = useState('1');
  const [xMotivo, setXMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  /* 15 caracteres é exigência do schema da Receita, não nossa -- avisar
     aqui evita a viagem até lá pra voltar recusado */
  const faltam = 15 - xMotivo.trim().length;

  async function confirmar() {
    setEnviando(true); setErro('');
    try {
      await cancelarNotaFiscal(nota.chave_acesso, { cMotivo, xMotivo: xMotivo.trim() });
      onConfirmado();
    } catch (e) {
      setErro(e.message);
      setEnviando(false);
    }
  }

  return (
    <div className={styles.modalFundo}>
      <div className={styles.modalCaixa}>
        <div className={styles.modalTopo}>
          <div>
            <h3 className={styles.modalTitulo}>Cancelar NFS-e</h3>
            <p className={styles.modalSub}>Não dá pra desfazer: a nota passa a constar como cancelada na Receita.</p>
          </div>
          <button type="button" className={styles.btnAvancado} onClick={onFechar}><FiX size={16} /></button>
        </div>

        <div className={styles.modalCorpo}>
          <div className={styles.resumoNota}>
            <div><strong>NFS-e nº {nota.numero_nfse || '—'}</strong> · {formatarCompetencia(nota.competencia)}</div>
            <div>{nota.tomador_nome} <span className={styles.campoLabel} style={{ fontWeight: 400 }}>
              {formatarDocumento(nota.tomador_documento)}</span></div>
            <div>{formatarMoeda(nota.valor_servico)} · emitida em {formatarDataHora(nota.data_emissao)}</div>
          </div>

          <span className={styles.campoLabel}>Motivo</span>
          <select className={styles.campoInput} value={cMotivo} onChange={e => setCMotivo(e.target.value)}>
            {MOTIVOS_CANCELAMENTO.map(m => <option key={m.codigo} value={m.codigo}>{m.nome}</option>)}
          </select>

          <span className={styles.campoLabel}>Justificativa</span>
          <textarea className={styles.campoTextarea} rows={3} value={xMotivo}
            onChange={e => setXMotivo(e.target.value)} maxLength={255}
            placeholder="Explique o que aconteceu — fica registrado na Receita." />
          <span className={styles.campoLabel} style={{ textTransform: 'none', fontWeight: 400 }}>
            {faltam > 0
              ? `Faltam ${faltam} caractere(s) — a Receita exige no mínimo 15.`
              : `${xMotivo.trim().length}/255 caracteres.`}
          </span>

          {erro && <p className={styles.erro} style={{ marginTop: 10 }}><FiAlertTriangle size={13} /> {erro}</p>}
        </div>

        <div className={styles.modalRodape}>
          <button type="button" className={styles.btnSecundario} onClick={onFechar} disabled={enviando}>
            Voltar
          </button>
          <button type="button" className={styles.btnEmitir} onClick={confirmar}
            disabled={enviando || faltam > 0}>
            {enviando ? 'Cancelando...' : 'Cancelar esta nota'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Arquivamento no Docs ──
   A nota emitida vai sozinha pra pasta do cliente no Docs. Quando isso não
   acontece, precisa aparecer: falha calada é indistinguível de "ainda não
   enviou", e foi o que custou caro na conciliação de setembro.

   Só a pendência que precisa de gente chama atenção. Enviado é um check
   discreto, e falha passageira não vira alarme porque o sistema reenvia
   sozinho. */
function SeloDocs({ nota }) {
  const s = nota.docs_situacao;
  if (!s) return null;

  if (s === 'pendente_humano') {
    return (
      <span className={`${styles.selo} ${styles.seloAtencao}`} title={nota.docs_erro || ''}>
        não arquivada
      </span>
    );
  }
  if (s === 'erro') {
    return (
      <span className={`${styles.selo} ${styles.seloNeutro}`} title={nota.docs_erro || ''}>
        reenviando
      </span>
    );
  }
  return (
    <span className={`${styles.selo} ${styles.seloOk}`} title="Arquivada na pasta do cliente no Docs">
      no Docs
    </span>
  );
}

/* ── Visualizador do DANFSe ──
   Conferir a nota é o caso comum; baixar é decisão de quem precisa guardar
   o arquivo. Por isso o PDF abre aqui dentro, e o download fica como botão
   dentro do visualizador. */
function ModalPdf({ nota, url, onFechar }) {
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  return (
    <div className={styles.modalFundo}>
      <div className={`${styles.modalCaixa} ${styles.modalCaixaLarga}`}>
        <div className={styles.modalTopo}>
          <div style={{ minWidth: 0 }}>
            <h3 className={styles.modalTitulo}>NFS-e nº {nota.numero_nfse || '—'}</h3>
            <p className={styles.modalSub}>
              {nota.cliente_codigo} {nota.cliente_nome} · {nota.tomador_nome} · {formatarMoeda(nota.valor_servico)}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <a className={styles.btnSecundario} href={url}
              download={`NFSe-${nota.numero_nfse || nota.chave_acesso}.pdf`}>
              <FiDownload size={13} /> Baixar
            </a>
            <button type="button" className={styles.btnAvancado} onClick={onFechar}><FiX size={16} /></button>
          </div>
        </div>
        <iframe className={styles.visualizadorPdf} src={url} title={`DANFSe ${nota.numero_nfse || ''}`} />
      </div>
    </div>
  );
}

/* ── Emitidas: todas as notas do escritório, por período ──
   Antes isso vivia dentro do cliente selecionado, uma lista por vez. Mas
   o escritório olha o mês inteiro -- quais notas saíram, de quem, e baixar
   tudo de uma vez pra guardar. Por isso a tela é global e o filtro é por
   período, com o mês corrente já aberto. */
function AbaEmitidas() {
  const [de, setDe] = useState(mesAtual());
  const [ate, setAte] = useState(mesAtual());
  const [notas, setNotas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [selecionadas, setSelecionadas] = useState(() => new Set());
  const [baixando, setBaixando] = useState(null);
  const [formatos, setFormatos] = useState(['xml']);
  const [cancelando, setCancelando] = useState(null); // nota
  const [pdfAberto, setPdfAberto] = useState(null);   // { nota, url }

  const carregar = useCallback(async () => {
    setCarregando(true); setErro(''); setSelecionadas(new Set());
    try {
      const r = await listarNotasPeriodo({ de: primeiroDia(de), ate: ultimoDia(ate) });
      setNotas(r.items || []);
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [de, ate]);

  useEffect(() => { carregar(); }, [carregar]);

  /* agrupado por cliente: é assim que o material é arquivado e entregue --
     a pasta de cada empresa, não uma lista corrida de notas */
  const grupos = useMemo(() => {
    const porCliente = new Map();
    for (const n of notas) {
      if (!porCliente.has(n.cliente_id)) {
        porCliente.set(n.cliente_id, {
          id: n.cliente_id, codigo: n.cliente_codigo, nome: n.cliente_nome, itens: [],
        });
      }
      porCliente.get(n.cliente_id).itens.push(n);
    }
    return [...porCliente.values()];
  }, [notas]);

  function alternar(chave) {
    setSelecionadas(s => {
      const nova = new Set(s);
      if (nova.has(chave)) nova.delete(chave); else nova.add(chave);
      return nova;
    });
  }

  function alternarGrupo(grupo) {
    const chaves = grupo.itens.map(n => n.chave_acesso);
    const todasMarcadas = chaves.every(c => selecionadas.has(c));
    setSelecionadas(s => {
      const nova = new Set(s);
      chaves.forEach(c => (todasMarcadas ? nova.delete(c) : nova.add(c)));
      return nova;
    });
  }

  function alternarFormato(f) {
    setFormatos(fs => (fs.includes(f) ? fs.filter(x => x !== f) : [...fs, f]));
  }

  /* marca que o documento já está guardado aqui: o backend o grava na
     primeira vez, e a linha passa a dizer que a próxima não depende da
     Receita */
  const marcarEmCache = (chave, formato) =>
    setNotas(ns => ns.map(n => n.chave_acesso === chave
      ? { ...n, [formato === 'xml' ? 'xml_em_cache' : 'pdf_em_cache']: true } : n));

  /* PDF abre na tela, não baixa: conferir a nota é o caso comum, baixar é
     decisão de quem precisa guardar o arquivo. O download fica dentro do
     visualizador. */
  async function abrirPdf(nota) {
    setBaixando(`${nota.chave_acesso}:pdf`); setErro('');
    try {
      const blob = await baixarDanfse(nota.cliente_id, nota.chave_acesso, 1);
      setPdfAberto({ nota, url: URL.createObjectURL(blob) });
      marcarEmCache(nota.chave_acesso, 'pdf');
    } catch (e) {
      setErro(e.message);
    } finally {
      setBaixando(null);
    }
  }

  async function baixarUma(nota, formato) {
    setBaixando(`${nota.chave_acesso}:${formato}`); setErro('');
    try {
      const blob = formato === 'xml'
        ? await baixarXmlNota(nota.cliente_id, nota.chave_acesso, 1)
        : await baixarDanfse(nota.cliente_id, nota.chave_acesso, 1);
      baixarBlob(blob, `NFSe-${nota.numero_nfse || nota.chave_acesso}.${formato}`);
      marcarEmCache(nota.chave_acesso, formato);
    } catch (e) {
      setErro(e.message);
    } finally {
      setBaixando(null);
    }
  }

  async function baixarLote() {
    if (!selecionadas.size || !formatos.length) return;
    setBaixando('lote'); setErro('');
    try {
      const blob = await baixarNotasEmLote([...selecionadas], formatos);
      baixarBlob(blob, `notas-fiscais-${de}${de === ate ? '' : `-a-${ate}`}.zip`);
    } catch (e) {
      setErro(e.message);
    } finally {
      setBaixando(null);
    }
  }

  const total = notas.reduce((s, n) => s + Number(n.valor_servico || 0), 0);

  return (
    <div>
      <div className={styles.barraFiltro}>
        <label className={styles.filtroCampo}>
          <span className={styles.campoLabel}>De</span>
          <input type="month" className={styles.campoInput} value={de} onChange={e => setDe(e.target.value)} />
        </label>
        <label className={styles.filtroCampo}>
          <span className={styles.campoLabel}>Até</span>
          <input type="month" className={styles.campoInput} value={ate} onChange={e => setAte(e.target.value)} />
        </label>
        <button type="button" className={styles.btnSecundario} onClick={carregar} disabled={carregando}>
          <FiRefreshCw size={13} className={carregando ? styles.girando : ''} /> Atualizar
        </button>
        <span className={styles.filtroResumo}>
          {notas.length} nota(s) · {formatarMoeda(total)}
        </span>
      </div>

      {erro && <p className={styles.erro}><FiAlertTriangle size={13} /> {erro}</p>}

      {selecionadas.size > 0 && (
        <div className={styles.barraSelecao}>
          <strong>{selecionadas.size} selecionada(s)</strong>
          <span className={styles.filtroResumo}>Baixar como:</span>
          {['xml', 'pdf'].map(f => (
            <label key={f} className={styles.escolhaFormato}>
              <input type="checkbox" checked={formatos.includes(f)} onChange={() => alternarFormato(f)} />
              {f.toUpperCase()}
            </label>
          ))}
          <button type="button" className={styles.btnPrimario}
            onClick={baixarLote} disabled={baixando === 'lote' || !formatos.length}>
            <FiPackage size={13} /> {baixando === 'lote' ? 'Montando o .zip...' : 'Baixar em lote'}
          </button>
          <button type="button" className={styles.btnAvancado} onClick={() => setSelecionadas(new Set())}>
            Limpar seleção
          </button>
        </div>
      )}

      {carregando ? (
        <p className={styles.campoLabel}>Carregando...</p>
      ) : grupos.length === 0 ? (
        <p className={styles.vazio}>Nenhuma nota emitida no período selecionado.</p>
      ) : (
        grupos.map(grupo => {
          const chaves = grupo.itens.map(n => n.chave_acesso);
          const todasMarcadas = chaves.every(c => selecionadas.has(c));
          const soma = grupo.itens.reduce((s, n) => s + Number(n.valor_servico || 0), 0);
          return (
            <div key={grupo.id} className={styles.grupoCliente}>
              <div className={styles.grupoTopo}>
                <label className={styles.grupoTitulo}>
                  <input type="checkbox" checked={todasMarcadas} onChange={() => alternarGrupo(grupo)} />
                  <strong style={{ color: '#6B8CAE', marginRight: 6 }}>{grupo.codigo}</strong>
                  {grupo.nome}
                </label>
                <span className={styles.filtroResumo}>
                  {grupo.itens.length} nota(s) · {formatarMoeda(soma)}
                </span>
              </div>

              <div className={styles.tabelaWrap}>
                <table className={styles.tabela}>
                  <thead>
                    <tr>
                      <th className={styles.colMarcar}></th>
                      <th className={styles.colNumero}>Nº NFS-e</th>
                      <th className={styles.colCompetencia}>Competência</th>
                      <th>Tomador</th>
                      <th className={styles.colValor}>Valor</th>
                      <th className={styles.colData}>Emitida em</th>
                      <th className={styles.colBotoes}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupo.itens.map(n => (
                      <tr key={n.chave_acesso}>
                        <td>
                          <input type="checkbox" checked={selecionadas.has(n.chave_acesso)}
                            onChange={() => alternar(n.chave_acesso)} />
                        </td>
                        <td className={styles.colNumero}>
                          <span className={styles.numeroNota}>{n.numero_nfse || '—'}</span>
                          {n.situacao === 'cancelada' && (
                            <span className={styles.selo} title={n.motivo_cancelamento || ''}>cancelada</span>
                          )}
                          <SeloDocs nota={n} />
                        </td>
                        <td className={styles.colCompetencia}>{formatarCompetencia(n.competencia)}</td>
                        {/* nome e documento na mesma linha: com a tela inteira
                            sobra largura, e empilhar deixava a linha com três
                            alturas diferentes */}
                        <td className={styles.colTomador}>
                          <span className={styles.tomadorNome}>{n.tomador_nome}</span>
                          <span className={styles.tomadorDoc}>{formatarDocumento(n.tomador_documento)}</span>
                        </td>
                        <td className={styles.colValor}>{formatarMoeda(n.valor_servico)}</td>
                        <td className={styles.colData}>{formatarDataHora(n.data_emissao)}</td>
                        <td className={styles.colBotoes}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button type="button" className={styles.btnSecundario}
                              onClick={() => abrirPdf(n)}
                              disabled={baixando === `${n.chave_acesso}:pdf`}
                              title={n.pdf_em_cache ? 'Abrir o DANFSe aqui na tela' : 'Gera o DANFSe a partir do XML e abre na tela'}>
                              <FiEye size={13} /> {baixando === `${n.chave_acesso}:pdf` ? '...' : 'PDF'}
                            </button>
                            <button type="button" className={styles.btnSecundario}
                              onClick={() => baixarUma(n, 'xml')}
                              disabled={baixando === `${n.chave_acesso}:xml`}
                              title={n.xml_em_cache ? 'XML guardado aqui' : 'Busca o XML na Receita e guarda'}>
                              <FiFileText size={13} /> {baixando === `${n.chave_acesso}:xml` ? '...' : 'XML'}
                            </button>
                            {n.situacao !== 'cancelada' && (
                              <button type="button" className={styles.btnCancelar}
                                onClick={() => setCancelando(n)} title="Cancelar esta NFS-e na Receita">
                                <FiSlash size={13} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}

      {pdfAberto && (
        <ModalPdf
          nota={pdfAberto.nota}
          url={pdfAberto.url}
          onFechar={() => { URL.revokeObjectURL(pdfAberto.url); setPdfAberto(null); }}
        />
      )}

      {cancelando && (
        <ModalCancelar
          nota={cancelando}
          onFechar={() => setCancelando(null)}
          onConfirmado={() => { setCancelando(null); carregar(); }}
        />
      )}
    </div>
  );
}

function Nf() {
  const [abaAtiva, setAbaAtiva] = useState('emitir');
  const [clientes, setClientes] = useState([]);
  const [clientePrestador, setClientePrestador] = useState(null);

  /* Emitidas e o formulário de emissão (com cliente escolhido) usam a tela
     inteira; só o convite inicial fica estreito. */
  const largo = abaAtiva === 'notas' || (abaAtiva === 'emitir' && !!clientePrestador);

  useEffect(() => {
    listarClientes().then(lista => {
      setClientes(lista);
      /* usuário de cliente: a lista é só ele -- abre direto no emissor */
      if (ehPortal() && lista[0]) setClientePrestador(lista[0]);
    }).catch(() => {});
  }, []);

  return (
    <div className={`${styles.page} ${largo ? styles.pageLarga : ''}`}>
      <div className={`${styles.tabBar} ${largo ? styles.barraLarga : ''}`}>
        {ABAS.map(aba => {
          const Icone = aba.icon;
          return (
            <button
              key={aba.id}
              type="button"
              className={`${styles.tab} ${abaAtiva === aba.id ? styles.tabAtiva : ''}`}
              onClick={() => setAbaAtiva(aba.id)}
            >
              <Icone size={14} /> {aba.label}
            </button>
          );
        })}
      </div>

      <div className={`${styles.conteudo} ${largo ? styles.conteudoLargo : ''}`}>
        {abaAtiva === 'emitir' && (
          clientePrestador
            ? <AssistenteEmissao prestador={clientePrestador} onTrocarPrestador={ehPortal() ? null : () => setClientePrestador(null)} />
            : (
              /* sem cliente escolhido não há o que preencher -- o
                 formulário inteiro fica fora da tela até ter em nome de
                 quem emitir */
              <div className={styles.convite}>
                <span className={styles.conviteIcone}><FiFileText size={22} /></span>
                <h2 className={styles.conviteTitulo}>Emissor de NFS-e</h2>
                <p className={styles.conviteTexto}>
                  Escolha em nome de qual cliente a nota será emitida para começar.
                </p>
                <ListaClientesEmissores clientes={clientes} onSelecionar={setClientePrestador} />
              </div>
            )
        )}
        {abaAtiva === 'notas' && <AbaEmitidas />}
      </div>

    </div>
  );
}

export default Nf;
